import {useEffect,useState} from 'react';
import {Routes,Route,Navigate,useNavigate} from 'react-router-dom';
import {Layout} from './components/Layout';
import {Dashboard} from './modules/Dashboard';
import {Settings} from './modules/Settings';
import {ClientsProducts} from './modules/ClientsProducts';
import {JobsDocuments} from './modules/JobsDocuments';
import {Invoices} from './modules/Invoices';
import {PaymentsRecurring} from './modules/PaymentsRecurring';
import {Reports} from './modules/Reports';
import {Diagnostics} from './modules/Diagnostics';
import {BusinessAdmin} from './modules/BusinessAdmin';
import {seedDatabase} from './data/mockData';
import {getConnectionStatus,isAuthenticated,initializePocketBase,logout} from './data/pocketbaseClient';
import {clearRepositoryReadCache,preloadWorkspace} from './data/repository';
import {processDueRecurringProfiles} from './utils/recurringInvoices';
import {LoginPage} from './components/LoginPage';
import {Button,Card} from './components/UI';

const STARTUP_HINTS=[
  'Keeping editing locked until the workspace is safe to use.',
  'Reusing secure records across screens to reduce repeat downloads.',
  'Large communication histories can take a little longer on the first load.',
  'Once ready, navigation uses the warmed workspace for a smoother experience.'
];

export default function App() {
  const navigate=useNavigate();
  const [isLoading,setIsLoading]=useState(true);
  const [authReady,setAuthReady]=useState(false);
  const [bootstrapVersion,setBootstrapVersion]=useState(0);
  const [startupError,setStartupError]=useState<string | null>(null);
  const [loadingHintIndex,setLoadingHintIndex]=useState(0);
  const [loadingStage,setLoadingStage]=useState({progress: 6,title: 'Starting InvoiceFlow',detail: 'Preparing your secure workspace.'});
  const {isMockMode}=getConnectionStatus();

  useEffect(()=> {
    let cancelled=false;
    const init=async ()=> {
      setStartupError(null);
      setIsLoading(true);
      setAuthReady(false);
      setLoadingHintIndex(0);
      setLoadingStage({progress: 6,title: 'Starting InvoiceFlow',detail: 'Preparing your secure workspace.'});
      if (isMockMode) {
        seedDatabase();
        if (!cancelled) {
          setLoadingStage({progress: 100,title: 'Workspace ready',detail: 'Local workspace is ready.'});
          setAuthReady(true);
          setIsLoading(false);
        }
        return;
      }

      setLoadingStage({progress: 8,title: 'Connecting securely',detail: 'Opening the PocketBase connection and restoring your session.'});
      const connectionAttempt=initializePocketBase().catch(error=> {
        console.warn('PocketBase health check did not complete during startup:',error);
        return null;
      });
      await Promise.resolve();

      if (!isAuthenticated()) {
        void connectionAttempt;
        if (!cancelled) {
          setAuthReady(true);
          setIsLoading(false);
        }
        return;
      }

      try {
        clearRepositoryReadCache();
        const summary=await preloadWorkspace(stage=> {
          if (!cancelled) setLoadingStage(stage);
        });
        if (!cancelled) {
          console.info('InvoiceFlow workspace preload completed:',summary);
          setLoadingStage({progress: 100,title: 'Workspace ready',detail: 'All working records are prepared. Opening InvoiceFlow now.'});
          await new Promise(resolve=> setTimeout(resolve,220));
        }
      } catch (error) {
        console.error('Unable to prepare InvoiceFlow workspace:',error);
        if (!cancelled) {
          setStartupError(error instanceof Error ? error.message : 'The workspace could not be loaded safely.');
          setAuthReady(true);
          setIsLoading(false);
        }
        return;
      }

      void connectionAttempt;
      if (!cancelled) {
        setAuthReady(true);
        setIsLoading(false);
      }
    };
    void init();
    return ()=> {
      cancelled=true;
    };
  },[bootstrapVersion,isMockMode]);

  useEffect(()=> {
    if (!isLoading) return;
    const interval=window.setInterval(()=> {
      setLoadingHintIndex(current=> (current + 1) % STARTUP_HINTS.length);
    },1800);
    return ()=> window.clearInterval(interval);
  },[isLoading]);

  useEffect(()=> {
    let signOutTimer: number | null=null;
    const handleAuthTransition=(event: Event)=> {
      const detail=(event as CustomEvent).detail;
      if (detail?.type !=='logout') return;
      if (signOutTimer) window.clearTimeout(signOutTimer);
      clearRepositoryReadCache();
      setStartupError(null);
      setLoadingHintIndex(0);
      setLoadingStage({progress: 100,title: 'Signing out securely',detail: 'Closing your authenticated workspace and returning to sign in.'});
      setIsLoading(true);
      setAuthReady(false);
      signOutTimer=window.setTimeout(()=> {
        setAuthReady(true);
        setIsLoading(false);
      },420);
    };
    window.addEventListener('invoiceflow:auth-transition',handleAuthTransition as EventListener);
    return ()=> {
      window.removeEventListener('invoiceflow:auth-transition',handleAuthTransition as EventListener);
      if (signOutTimer) window.clearTimeout(signOutTimer);
    };
  },[]);

  useEffect(()=> {
    if (isLoading || !authReady || (!isMockMode && !isAuthenticated())) return;
    let disposed=false;
    const checkRecurring=async ()=> {
      try {
        const summary=await processDueRecurringProfiles();
        if (!disposed && (summary.generated > 0 || summary.reconciled > 0)) {
          console.info('Recurring invoice check completed:',summary);
        }
        if (!disposed && summary.failed > 0) {
          console.warn('Recurring invoice check completed with failures:',summary.messages);
        }
      } catch (error) {
        if (!disposed) console.warn('Recurring invoice browser check failed:',error);
      }
    };
    const handleFocus=()=> {void checkRecurring();};
    const handleVisibility=()=> {if (document.visibilityState==='visible') void checkRecurring();};
    const initialTimer=window.setTimeout(()=> {void checkRecurring();},6000);
    const interval=window.setInterval(()=> {void checkRecurring();},15 * 60 * 1000);
    window.addEventListener('focus',handleFocus);
    document.addEventListener('visibilitychange',handleVisibility);
    return ()=> {
      disposed=true;
      window.clearTimeout(initialTimer);
      window.clearInterval(interval);
      window.removeEventListener('focus',handleFocus);
      document.removeEventListener('visibilitychange',handleVisibility);
    };
  },[isLoading,authReady,isMockMode]);

  if (isLoading || !authReady) {
    return (
      <div className="min-h-[100dvh] bg-slate-950 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.06] p-6 sm:p-8 shadow-2xl backdrop-blur-xl">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-500 text-white shadow-lg shadow-brand-500/20">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-brand-300">InvoiceFlow Studio</p>
              <h1 className="mt-1 text-xl font-black text-white">{loadingStage.title}</h1>
            </div>
          </div>
          <p className="mt-5 text-sm leading-6 text-slate-300">{loadingStage.detail}</p>
          <div className="mt-6 h-2 overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full bg-brand-500 transition-all duration-500" style={{width: `${Math.max(6,Math.min(100,loadingStage.progress))}%`}} />
          </div>
          <div className="mt-3 flex items-center justify-between gap-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">
            <span>Secure workspace sync</span>
            <span>{loadingStage.progress}%</span>
          </div>
          <div className="mt-6 rounded-2xl border border-white/10 bg-black/20 p-4">
            <p className="text-xs leading-5 text-slate-400">{STARTUP_HINTS[loadingHintIndex]}</p>
          </div>
          <p className="mt-4 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600">Editing and navigation remain locked until workspace records are ready</p>
        </div>
      </div>
    );
  }

  if (startupError && !isMockMode && isAuthenticated()) {
    return (
      <div className="min-h-[100dvh] bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg p-6 sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-rose-600">Workspace sync interrupted</p>
          <h2 className="mt-2 text-xl font-black text-gray-900">InvoiceFlow has not enabled editing.</h2>
          <p className="mt-3 text-sm leading-6 text-gray-600">The PocketBase workspace did not finish loading safely, so the app has stayed locked rather than exposing incomplete records.</p>
          <p className="mt-3 break-words rounded-xl bg-rose-50 p-3 text-xs font-semibold text-rose-700">{startupError}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button className="w-full" onClick={()=> {clearRepositoryReadCache();setBootstrapVersion(value=> value + 1);}}>Retry workspace sync</Button>
            <Button variant="secondary" className="w-full" onClick={()=> {setStartupError(null);logout();navigate('/');}}>Return to sign in</Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!isMockMode && !isAuthenticated()) {
    return <LoginPage onLogin={()=> {clearRepositoryReadCache();setStartupError(null);setLoadingStage({progress: 8,title: 'Signing in securely',detail: 'Authentication succeeded. Preparing your PocketBase workspace now.'});setIsLoading(true);setAuthReady(false);navigate('/');setBootstrapVersion(value=> value + 1);}} />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/clients-products" element={<ClientsProducts />} />
        <Route path="/jobs-documents" element={<JobsDocuments />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/payments-recurring" element={<PaymentsRecurring />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/business-admin" element={<BusinessAdmin />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/diagnostics" element={<Diagnostics />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}